/**
 * Workflows — an organization's saved pipelines of steps, drawn as a chart.
 *
 * A workflow is a graph: steps (nodes) joined by links (edges). A run walks
 * a frozen copy of that graph; each step it reaches records a step run with
 * its status, output and, when it fails, a reason a person can read. Links
 * may leave a step from a named outlet (`sourceHandle`), which is how
 * branching steps choose a path: a Condition step leaves by "yes" or "no".
 * Several links out of one step run side by side, and a step with several
 * links in waits for all of them, so parallel paths and joins need no steps
 * of their own. An Approval step pauses the run until one of the people it
 * names approves, rejects, or sends feedback with a step to run again from.
 * An Ingestion step reads what reaches it into a fixed set of named fields,
 * which later steps use as `{{Step name.Field name}}`.
 *
 * Wire shapes only: the graph rules live in `@repo/shared/workflowGraph`, the
 * store and runner in `apps/server/src/amoreg/workflows/`.
 *
 * @module workflows
 */
import * as Schema from "effect/Schema";

import { IsoDateTime, NonNegativeInt, TrimmedNonEmptyString } from "./baseSchemas.ts";
import { BrowserTaskSnapshot } from "./browserTasks.ts";

/** Limits a graph must stay within; the server refuses anything larger. */
export const WORKFLOW_LIMITS = {
  nodes: 100,
  edges: 300,
  labelLength: 120,
  instructionsLength: 20_000,
  inputLength: 50_000,
  nameLength: 200,
  descriptionLength: 2_000,
  approvers: 20,
  /** People one Notify or Send email step names. */
  recipients: 50,
  /** People one workflow is shared with. */
  sharedPeople: 50,
  /** Sources one Intake step reads, and documents picked in one source. */
  intakeSources: 10,
  intakePicks: 20,
  commentLength: 5_000,
  ingestionFields: 40,
  fieldNameLength: 80,
  fieldDescriptionLength: 500,
  /** The most of a sample file's text the field suggestions read. */
  sampleTextLength: 60_000,
  /** Tables one step's result carries, and the size of each (AMO-299). */
  stepTables: 20,
  tableRows: 2_000,
  tableColumns: 50,
  tableCellText: 1_000,
  /** Cells across every table of one step's result. */
  stepTableCells: 40_000,
  /** Rules one Compare step checks (AMO-301). */
  compareRules: 20,
  /** Why a workflow is locked (AMO-305). */
  lockReasonLength: 200,
  /** Files one run is handed in the Run dialog, and the largest (AMO-304). */
  runFiles: 3,
  runFileBytes: 4 * 1024 * 1024,
  /** The request eve passes to the draft model, in her words. */
  draftRequestLength: 4_000,
} as const;

export const WorkflowElementId = TrimmedNonEmptyString.check(Schema.isMaxLength(64));
const WorkflowLabel = TrimmedNonEmptyString.check(Schema.isMaxLength(WORKFLOW_LIMITS.labelLength));

export const WorkflowPosition = Schema.Struct({ x: Schema.Finite, y: Schema.Finite });
export type WorkflowPosition = typeof WorkflowPosition.Type;

/** The chat models a Prompt step can run on: the composer's standard and Pro. */
export const WorkflowPromptModel = Schema.Literals(["standard", "pro"]);
export type WorkflowPromptModel = typeof WorkflowPromptModel.Type;

export const WorkflowPromptEffort = Schema.Literals(["low", "medium", "high", "xhigh"]);
export type WorkflowPromptEffort = typeof WorkflowPromptEffort.Type;

/** Where a run begins; what the person types when they press Run arrives here. */
export const WorkflowStartNode = Schema.Struct({
  id: WorkflowElementId,
  type: Schema.Literal("start"),
  label: WorkflowLabel,
  position: WorkflowPosition,
  config: Schema.Struct({
    /** What the Run dialog asks for, e.g. "Client name and the period to cover". */
    inputHint: Schema.optional(Schema.String.check(Schema.isMaxLength(500))),
    /** The Run dialog takes files too (AMO-304), e.g. "Today's manager trade file". */
    files: Schema.optional(Schema.Struct({ ask: Schema.Boolean, hint: Schema.String.check(Schema.isMaxLength(200)) })),
  }),
});

/** One chat-model turn over the run's input and earlier steps' outputs. */
export const WorkflowPromptNode = Schema.Struct({
  id: WorkflowElementId,
  type: Schema.Literal("prompt"),
  label: WorkflowLabel,
  position: WorkflowPosition,
  config: Schema.Struct({
    instructions: Schema.String.check(Schema.isMaxLength(WORKFLOW_LIMITS.instructionsLength)),
    model: WorkflowPromptModel,
    effort: Schema.optional(WorkflowPromptEffort),
  }),
});

/** The run's result: the outputs that reach it, in order. */
export const WorkflowOutputNode = Schema.Struct({
  id: WorkflowElementId,
  type: Schema.Literal("output"),
  label: WorkflowLabel,
  position: WorkflowPosition,
  config: Schema.Struct({}),
});

/** A Condition step's two outlets. */
export const WORKFLOW_CONDITION_OUTLETS = ["yes", "no"] as const;
export type WorkflowConditionOutlet = (typeof WORKFLOW_CONDITION_OUTLETS)[number];

/**
 * A fork: the run continues by "yes" or "no". `ask` puts a yes/no question to
 * the AI about the earlier steps' results (a chat turn, billed like a Prompt
 * step); `contains` checks the results for a piece of text (instant, free).
 */
export const WorkflowConditionNode = Schema.Struct({
  id: WorkflowElementId,
  type: Schema.Literal("condition"),
  label: WorkflowLabel,
  position: WorkflowPosition,
  config: Schema.Struct({
    mode: Schema.Literals(["ask", "contains"]),
    /** `ask`: the yes/no question. */
    question: Schema.String.check(Schema.isMaxLength(WORKFLOW_LIMITS.instructionsLength)),
    /** `contains`: the text to look for (case-insensitive). */
    text: Schema.String.check(Schema.isMaxLength(500)),
    model: WorkflowPromptModel,
    effort: Schema.optional(WorkflowPromptEffort),
  }),
});

/**
 * A saved browser task the browser helper carries out (AMO-155): reading and
 * downloading only, on the task's allowed sites, in a chat of the person who
 * ran the workflow, with their saved logins.
 */
export const WorkflowBrowserTaskNode = Schema.Struct({
  id: WorkflowElementId,
  type: Schema.Literal("browser-task"),
  label: WorkflowLabel,
  position: WorkflowPosition,
  config: Schema.Struct({
    /** The browser task; empty until one is chosen. */
    taskId: Schema.String.check(Schema.isMaxLength(64)),
    /** The values the task needs, written with `{{input}}` and `{{Step name}}`. */
    inputs: Schema.String.check(Schema.isMaxLength(WORKFLOW_LIMITS.instructionsLength)),
    /**
     * The task as this run uses it. Set by the server on a run's frozen copy
     * of the graph when the run starts; a saved workflow never carries it.
     */
    task: Schema.optional(BrowserTaskSnapshot),
  }),
});

/**
 * A pause for a person. The run waits until one of `approverEmails` (never the
 * person who started the run) approves, rejects, or sends feedback naming an
 * earlier step to run again from. On approval it passes on what reached it.
 */
export const WorkflowApprovalNode = Schema.Struct({
  id: WorkflowElementId,
  type: Schema.Literal("approval"),
  label: WorkflowLabel,
  position: WorkflowPosition,
  config: Schema.Struct({
    approverEmails: Schema.Array(Schema.String.check(Schema.isMaxLength(320))).check(
      Schema.isMaxLength(WORKFLOW_LIMITS.approvers),
    ),
    /** What the approver should check, shown to them beside the results. */
    instructions: Schema.String.check(Schema.isMaxLength(2_000)),
  }),
});

/**
 * A manual step (AMO-307): something done outside Amoreg, such as importing
 * a file into the accounting system. The run waits until one of the people
 * it names (nobody named: the person who started the run) marks it done,
 * with an optional note. Who ticked it and when is kept with the run.
 */
export const WorkflowChecklistNode = Schema.Struct({
  id: WorkflowElementId,
  type: Schema.Literal("checklist"),
  label: WorkflowLabel,
  position: WorkflowPosition,
  config: Schema.Struct({
    assigneeEmails: Schema.Array(Schema.String.check(Schema.isMaxLength(320))).check(
      Schema.isMaxLength(WORKFLOW_LIMITS.approvers),
    ),
    /** What to do, shown to them beside the results that reached the step. */
    instructions: Schema.String.check(Schema.isMaxLength(2_000)),
  }),
});

/** One field an Ingestion step reads: its name, and what it is, for the AI to find it. */
export const WorkflowIngestionField = Schema.Struct({
  name: Schema.String.check(Schema.isMaxLength(WORKFLOW_LIMITS.fieldNameLength)),
  description: Schema.String.check(Schema.isMaxLength(WORKFLOW_LIMITS.fieldDescriptionLength)),
});
export type WorkflowIngestionField = typeof WorkflowIngestionField.Type;

/**
 * What an Ingestion step reads its fields into: one set for the whole
 * material ("fields", the default), or a row per item the material lists
 * ("rows": each trade, holding or balance a row of a table, AMO-299).
 */
export const WorkflowIngestionShape = Schema.Literals(["fields", "rows"]);
export type WorkflowIngestionShape = typeof WorkflowIngestionShape.Type;

/**
 * Turns what reaches it into a fixed set of text fields (a chat turn, billed
 * like a Prompt step). A field the AI cannot find is left empty and the run
 * carries on. Its result is `template` with each `{{Field name}}` filled, or,
 * with no template, the fields one per line. Read as rows, its result is a
 * table with a column per field (the template is not used).
 */
export const WorkflowIngestionNode = Schema.Struct({
  id: WorkflowElementId,
  type: Schema.Literal("ingestion"),
  label: WorkflowLabel,
  position: WorkflowPosition,
  config: Schema.Struct({
    fields: Schema.Array(WorkflowIngestionField).check(Schema.isMaxLength(WORKFLOW_LIMITS.ingestionFields)),
    template: Schema.String.check(Schema.isMaxLength(WORKFLOW_LIMITS.instructionsLength)),
    model: WorkflowPromptModel,
    effort: Schema.optional(WorkflowPromptEffort),
    /** Absent: "fields", as every step saved before AMO-299. */
    shape: Schema.optional(WorkflowIngestionShape),
  }),
});

/** A table an earlier step passes on: that step, and the table's name (empty: its first table). */
export const WorkflowTableRef = Schema.Struct({
  step: Schema.String.check(Schema.isMaxLength(64)),
  table: Schema.String.check(Schema.isMaxLength(200)),
});
export type WorkflowTableRef = typeof WorkflowTableRef.Type;

/**
 * What one Compare rule flags (AMO-301): `percent` and `absolute` a numeric
 * difference past `limit` (a percentage of the first table's value, or an
 * amount); `equal` values that differ; `unchanged` values that did not move
 * (a stale price).
 */
export const WORKFLOW_COMPARE_CHECKS = ["percent", "absolute", "equal", "unchanged"] as const;
export const WorkflowCompareCheck = Schema.Literals(WORKFLOW_COMPARE_CHECKS);
export type WorkflowCompareCheck = typeof WorkflowCompareCheck.Type;

/** One rule: a column of the first table, the column of the second it is checked against (empty: the same name), how, and the limit. */
export const WorkflowCompareRule = Schema.Struct({
  column: Schema.String.check(Schema.isMaxLength(WORKFLOW_LIMITS.fieldNameLength)),
  against: Schema.String.check(Schema.isMaxLength(WORKFLOW_LIMITS.fieldNameLength)),
  check: WorkflowCompareCheck,
  /** `percent` and `absolute` only: how far apart is too far. */
  limit: Schema.Number.check(Schema.isGreaterThanOrEqualTo(0)),
});
export type WorkflowCompareRule = typeof WorkflowCompareRule.Type;

/**
 * Compares two tables earlier steps passed on, row by row matched on a key
 * column, by fixed rules (no AI, nothing billed): what breaks a rule, a row
 * only one table has, and a key that repeats become rows of an Exceptions
 * table. It leaves by Yes when there are exceptions and No when there are
 * none, as a Condition does (AMO-301).
 */
export const WorkflowCompareNode = Schema.Struct({
  id: WorkflowElementId,
  type: Schema.Literal("compare"),
  label: WorkflowLabel,
  position: WorkflowPosition,
  config: Schema.Struct({
    left: WorkflowTableRef,
    right: WorkflowTableRef,
    /** The column rows are matched on, in the first table; `rightKey` names it in the second when it differs. */
    key: Schema.String.check(Schema.isMaxLength(WORKFLOW_LIMITS.fieldNameLength)),
    rightKey: Schema.String.check(Schema.isMaxLength(WORKFLOW_LIMITS.fieldNameLength)),
    rules: Schema.Array(WorkflowCompareRule).check(Schema.isMaxLength(WORKFLOW_LIMITS.compareRules)),
    /** Flag a row only one of the tables has. */
    missing: Schema.Boolean,
  }),
});

/**
 * For each row (AMO-303): the same instructions carried out for every row of
 * a table an earlier step passed on, each row's `{{row.Column}}` filled with
 * its values, and the answers gathered into a table (the row and its Answer).
 * One AI turn for every row, billed as one Prompt step.
 */
export const WorkflowForEachNode = Schema.Struct({
  id: WorkflowElementId,
  type: Schema.Literal("for-each"),
  label: WorkflowLabel,
  position: WorkflowPosition,
  config: Schema.Struct({
    source: WorkflowTableRef,
    instructions: Schema.String.check(Schema.isMaxLength(WORKFLOW_LIMITS.instructionsLength)),
    model: WorkflowPromptModel,
    effort: Schema.optional(WorkflowPromptEffort),
    /** At most this many rows, from the top (1 to 50). */
    maxRows: Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 50 })),
  }),
});

/** Where an Intake step reads from (AMO-169). Email reads the mailbox of the person a run acts for, with their permission (AMO-158). */
export const WORKFLOW_INTAKE_SOURCE_KINDS = ["library", "space", "sharepoint", "googledrive", "email"] as const;
export const WorkflowIntakeSourceKind = Schema.Literals(WORKFLOW_INTAKE_SOURCE_KINDS);
export type WorkflowIntakeSourceKind = typeof WorkflowIntakeSourceKind.Type;

/** One document or folder picked in a source. */
export const WorkflowIntakePick = Schema.Struct({
  id: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(512)),
  name: Schema.String.check(Schema.isMaxLength(300)),
  /** A folder: the files directly in it are read (not its subfolders). */
  isFolder: Schema.optional(Schema.Boolean),
  /** SharePoint: the folder's path in its library, to list it. */
  path: Schema.optional(Schema.String.check(Schema.isMaxLength(1_000))),
});
export type WorkflowIntakePick = typeof WorkflowIntakePick.Type;

/** The mailboxes an email source can read: the person's own connected Gmail or Outlook. */
export const WORKFLOW_MAIL_APPS = ["gmail", "outlook"] as const;
export const WorkflowMailApp = Schema.Literals(WORKFLOW_MAIL_APPS);
export type WorkflowMailApp = typeof WorkflowMailApp.Type;

/**
 * What an email source narrows its search by. At least one of sender,
 * subject or label is required, and only mail from the last `days` days is
 * read (at most 90).
 */
export const WorkflowIntakeMailFilter = Schema.Struct({
  from: Schema.String.check(Schema.isMaxLength(200)),
  subject: Schema.String.check(Schema.isMaxLength(200)),
  /** A Gmail label, or an Outlook folder. */
  label: Schema.String.check(Schema.isMaxLength(100)),
  days: Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 90 })),
  /** Read the matched messages' attached files too (AMO-302): PDF, Word, text, CSV and Excel, spreadsheets as tables. */
  attachments: Schema.optional(Schema.Boolean),
  /** Only attached files whose name contains this (ignoring case), e.g. `.xlsx` or `trades`; empty reads every one. */
  attachmentName: Schema.optional(Schema.String.check(Schema.isMaxLength(100))),
});
export type WorkflowIntakeMailFilter = typeof WorkflowIntakeMailFilter.Type;

/**
 * One source an Intake step reads: the documents picked in it, a search, or
 * both. `location` is the space (Space files), the site URL (SharePoint; empty
 * when the organization allowed one location), a folder to search in
 * (Google Drive; empty for every allowed folder) or the mailbox app (email:
 * `gmail` or `outlook`); the library has none. The search may use `{{input}}`
 * and `{{Step name}}`; an email source's search adds words to its `mail` filter.
 */
export const WorkflowIntakeSource = Schema.Struct({
  kind: WorkflowIntakeSourceKind,
  location: Schema.String.check(Schema.isMaxLength(500)),
  picks: Schema.Array(WorkflowIntakePick).check(Schema.isMaxLength(WORKFLOW_LIMITS.intakePicks)),
  query: Schema.String.check(Schema.isMaxLength(500)),
  mail: Schema.optional(WorkflowIntakeMailFilter),
});
export type WorkflowIntakeSource = typeof WorkflowIntakeSource.Type;

/**
 * Gathers material from named sources and passes on its text, each piece
 * headed by where it came from, read as the person the run acts for.
 */
export const WorkflowIntakeNode = Schema.Struct({
  id: WorkflowElementId,
  type: Schema.Literal("intake"),
  label: WorkflowLabel,
  position: WorkflowPosition,
  config: Schema.Struct({
    sources: Schema.Array(WorkflowIntakeSource).check(Schema.isMaxLength(WORKFLOW_LIMITS.intakeSources)),
  }),
});

/** A SharePoint or Google Drive folder's contents (or the allowed places), for the Intake editor's picker. */
export const WorkflowIntakeBrowseResponse = Schema.Struct({
  items: Schema.Array(
    Schema.Struct({
      id: Schema.String,
      name: Schema.String,
      isFolder: Schema.Boolean,
      /** SharePoint: a folder's path in its library; a location's site URL. */
      path: Schema.optional(Schema.String),
      /** A place to open rather than pick: an allowed SharePoint location or Drive folder. */
      isLocation: Schema.optional(Schema.Boolean),
    }),
  ),
});
export type WorkflowIntakeBrowseResponse = typeof WorkflowIntakeBrowseResponse.Type;

/* Destinations (AMO-166): where a run's results go. Each takes what is linked into it. */

const WorkflowRecipientEmails = Schema.Array(Schema.String.check(Schema.isMaxLength(320))).check(
  Schema.isMaxLength(WORKFLOW_LIMITS.recipients),
);

/**
 * Opens a chat for the person the run acts for, its first message the results
 * (no AI turn), for them to follow up on.
 */
export const WorkflowChatNode = Schema.Struct({
  id: WorkflowElementId,
  type: Schema.Literal("chat"),
  label: WorkflowLabel,
  position: WorkflowPosition,
  config: Schema.Struct({
    /** The chat's title; empty: the workflow's name and the date. */
    title: Schema.String.check(Schema.isMaxLength(WORKFLOW_LIMITS.labelLength)),
  }),
});

/** An Inbox notice to members it names, linking to the run, with the results. */
export const WorkflowNotifyNode = Schema.Struct({
  id: WorkflowElementId,
  type: Schema.Literal("notify"),
  label: WorkflowLabel,
  position: WorkflowPosition,
  config: Schema.Struct({
    recipientEmails: WorkflowRecipientEmails,
    /** The notice's title; empty: "<workflow> finished". */
    title: Schema.String.check(Schema.isMaxLength(160)),
  }),
});

/** What Save to space makes: a Markdown document, or an Excel workbook of the tables linked in (AMO-300). */
/** `word` (AMO-309): a page in the space, kept with a `.docx` of it to download. */
export const WorkflowSaveFileFormat = Schema.Literals(["markdown", "excel", "word"]);
export type WorkflowSaveFileFormat = typeof WorkflowSaveFileFormat.Type;

/** Saves the results as a new document in a space, one per run: Markdown, or an Excel workbook. */
export const WorkflowSaveFileNode = Schema.Struct({
  id: WorkflowElementId,
  type: Schema.Literal("save-file"),
  label: WorkflowLabel,
  position: WorkflowPosition,
  config: Schema.Struct({
    /** The space it saves into; empty until one is chosen. */
    spaceId: Schema.String.check(Schema.isMaxLength(128)),
    /** The document's name before the date; empty: the workflow's name. */
    title: Schema.String.check(Schema.isMaxLength(WORKFLOW_LIMITS.labelLength)),
    /** Absent: "markdown", as every step saved before AMO-300. */
    format: Schema.optional(WorkflowSaveFileFormat),
  }),
});

/** Emails the results to members it names, as the person the run acts for. */
export const WorkflowEmailNode = Schema.Struct({
  id: WorkflowElementId,
  type: Schema.Literal("email"),
  label: WorkflowLabel,
  position: WorkflowPosition,
  config: Schema.Struct({
    recipientEmails: WorkflowRecipientEmails,
    /** The subject; empty: the workflow's name and the date. */
    subject: Schema.String.check(Schema.isMaxLength(200)),
  }),
});

export const WorkflowNode = Schema.Union([
  WorkflowStartNode,
  WorkflowPromptNode,
  WorkflowConditionNode,
  WorkflowCompareNode,
  WorkflowForEachNode,
  WorkflowBrowserTaskNode,
  WorkflowApprovalNode,
  WorkflowChecklistNode,
  WorkflowIngestionNode,
  WorkflowIntakeNode,
  WorkflowChatNode,
  WorkflowNotifyNode,
  WorkflowSaveFileNode,
  WorkflowEmailNode,
  WorkflowOutputNode,
]);
export type WorkflowNode = typeof WorkflowNode.Type;
export type WorkflowNodeType = WorkflowNode["type"];

export const WorkflowEdge = Schema.Struct({
  id: WorkflowElementId,
  source: WorkflowElementId,
  target: WorkflowElementId,
  /** The outlet the link leaves from; absent for a step's single default outlet. */
  sourceHandle: Schema.optional(WorkflowElementId),
});
export type WorkflowEdge = typeof WorkflowEdge.Type;

export const WorkflowGraph = Schema.Struct({
  nodes: Schema.Array(WorkflowNode).check(Schema.isMaxLength(WORKFLOW_LIMITS.nodes)),
  edges: Schema.Array(WorkflowEdge).check(Schema.isMaxLength(WORKFLOW_LIMITS.edges)),
  /**
   * How its steps are laid out: "down" (top to bottom, AMO-177). A graph
   * without it was laid out left to right, and the chart lays it out again
   * the first time it opens.
   */
  layout: Schema.optional(Schema.Literal("down")),
});
export type WorkflowGraph = typeof WorkflowGraph.Type;

/** Why a graph cannot run, attached to the step or link it is about. */
export const WorkflowGraphIssue = Schema.Struct({
  code: TrimmedNonEmptyString,
  message: TrimmedNonEmptyString,
  nodeId: Schema.optional(Schema.String),
  edgeId: Schema.optional(Schema.String),
});
export type WorkflowGraphIssue = typeof WorkflowGraphIssue.Type;

export const WorkflowRunStatus = Schema.Literals([
  "queued",
  "running",
  "waiting",
  "succeeded",
  "failed",
  "cancelled",
]);
export type WorkflowRunStatus = typeof WorkflowRunStatus.Type;

export const WorkflowStepStatus = Schema.Literals([
  "pending",
  "running",
  "waiting",
  "succeeded",
  "failed",
  "skipped",
  "cancelled",
]);
export type WorkflowStepStatus = typeof WorkflowStepStatus.Type;

/** A failure as a person reads it, with the technical detail kept behind it. */
export const WorkflowStepError = Schema.Struct({
  message: TrimmedNonEmptyString,
  detail: Schema.NullOr(Schema.String),
});
export type WorkflowStepError = typeof WorkflowStepError.Type;

/** One field an Ingestion step read; `value` is null when the AI could not find it. */
export const WorkflowStepField = Schema.Struct({
  name: Schema.String,
  value: Schema.NullOr(Schema.String),
});
export type WorkflowStepField = typeof WorkflowStepField.Type;

/** One cell of a step's table: text, a number, or empty. */
export const WorkflowTableCell = Schema.NullOr(Schema.Union([Schema.String, Schema.Number]));
export type WorkflowTableCell = typeof WorkflowTableCell.Type;

/**
 * A table a step's result carries beside its text (AMO-299): a sheet an
 * Intake step read, or the rows an Ingestion step read. Every row has a cell
 * per column. `omittedRows` counts the rows past the limit that were not kept.
 */
export const WorkflowStepTable = Schema.Struct({
  name: Schema.String,
  columns: Schema.Array(Schema.String),
  rows: Schema.Array(Schema.Array(WorkflowTableCell)),
  omittedRows: Schema.optional(NonNegativeInt),
});
export type WorkflowStepTable = typeof WorkflowStepTable.Type;

/** Where a succeeded destination step delivered its results, for the run's panel to link. */
export const WorkflowStepDelivery = Schema.Union([
  Schema.Struct({ kind: Schema.Literal("chat"), threadId: Schema.String }),
  Schema.Struct({
    kind: Schema.Literal("file"),
    spaceId: Schema.String,
    docId: Schema.String,
    title: Schema.String,
    /** `word`: the page was saved with a Word file of it, downloaded as the document's original (AMO-309). */
    download: Schema.optional(Schema.Literals(["word"])),
  }),
  Schema.Struct({ kind: Schema.Literal("notice"), recipients: Schema.Array(Schema.String) }),
  Schema.Struct({
    kind: Schema.Literal("email"),
    recipients: Schema.Array(Schema.String),
    /** Named on the step but not reached: the provider declined, or no longer members. */
    missed: Schema.Array(Schema.String),
  }),
]);
export type WorkflowStepDelivery = typeof WorkflowStepDelivery.Type;

/** An earlier attempt of a step, replaced when it ran again (AMO-308): how it went, its result clipped. */
export const WorkflowStepAttempt = Schema.Struct({
  attempt: NonNegativeInt,
  status: WorkflowStepStatus,
  output: Schema.NullOr(Schema.String),
  errorMessage: Schema.NullOr(Schema.String),
  startedAt: Schema.NullOr(IsoDateTime),
  finishedAt: Schema.NullOr(IsoDateTime),
});
export type WorkflowStepAttempt = typeof WorkflowStepAttempt.Type;

export const WorkflowStepRun = Schema.Struct({
  nodeId: Schema.String,
  status: WorkflowStepStatus,
  attempt: NonNegativeInt,
  output: Schema.NullOr(Schema.String),
  error: Schema.NullOr(WorkflowStepError),
  startedAt: Schema.NullOr(IsoDateTime),
  finishedAt: Schema.NullOr(IsoDateTime),
  /** A succeeded Ingestion step's fields, in the step's order; absent otherwise and from older servers. */
  fields: Schema.optional(Schema.Array(WorkflowStepField)),
  /** The tables a succeeded step's result carries (AMO-299); absent when it has none, and from older servers. */
  tables: Schema.optional(Schema.Array(WorkflowStepTable)),
  /** A Browser task step's chat: the run's person can open it to watch or sign in (AMO-155). */
  chatThreadId: Schema.optional(Schema.String),
  /** A succeeded destination step's delivery (AMO-166); absent otherwise and from older servers. */
  delivery: Schema.optional(WorkflowStepDelivery),
  /** The outlets a finished Condition step took ("yes" or "no"); absent for other steps and from older servers. */
  chosenHandles: Schema.optional(Schema.Array(Schema.String)),
  /** The attempts it finished before it ran again, newest first (AMO-308); absent when there are none. */
  earlierAttempts: Schema.optional(Schema.Array(WorkflowStepAttempt)),
});
export type WorkflowStepRun = typeof WorkflowStepRun.Type;

export const WorkflowRunSummary = Schema.Struct({
  id: Schema.String,
  workflowId: Schema.String,
  status: WorkflowRunStatus,
  startedByEmail: Schema.NullOr(Schema.String),
  createdAt: IsoDateTime,
  finishedAt: Schema.NullOr(IsoDateTime),
  error: Schema.NullOr(Schema.String),
});
export type WorkflowRunSummary = typeof WorkflowRunSummary.Type;

/** `done`: a manual step marked done (AMO-307). */
export const WorkflowApprovalDecisionKind = Schema.Literals(["approve", "reject", "feedback", "done"]);
export type WorkflowApprovalDecisionKind = typeof WorkflowApprovalDecisionKind.Type;

/** One decision on an Approval step, kept for the run's history. */
export const WorkflowApprovalDecision = Schema.Struct({
  nodeId: Schema.String,
  /** The Approval step's attempt the decision was made on. */
  attempt: NonNegativeInt,
  decision: WorkflowApprovalDecisionKind,
  byEmail: Schema.NullOr(Schema.String),
  comment: Schema.NullOr(Schema.String),
  /** Feedback: the step the run went back to. */
  rerunFrom: Schema.NullOr(Schema.String),
  decidedAt: IsoDateTime,
});
export type WorkflowApprovalDecision = typeof WorkflowApprovalDecision.Type;

/**
 * One read of a person's mailbox by an email source, as the run's log shows
 * it: whose, which app, the search sent, how many messages came back.
 */
export const WorkflowMailboxRead = Schema.Struct({
  nodeId: Schema.String,
  personEmail: Schema.String,
  app: WorkflowMailApp,
  search: Schema.String,
  messageCount: NonNegativeInt,
  readAt: IsoDateTime,
  /** When the email text this read passed on was removed from the run (after 14 days). */
  trimmedAt: Schema.NullOr(IsoDateTime),
});
export type WorkflowMailboxRead = typeof WorkflowMailboxRead.Type;

export const WorkflowRun = Schema.Struct({
  ...WorkflowRunSummary.fields,
  input: Schema.NullOr(Schema.String),
  /** The graph as it was when the run started; editing the workflow never changes it. */
  graph: WorkflowGraph,
  steps: Schema.Array(WorkflowStepRun),
  /** Every Approval decision, oldest first; absent from older servers. */
  decisions: Schema.optional(Schema.Array(WorkflowApprovalDecision)),
  /** Every mailbox read of the run's email sources, oldest first; absent from older servers. */
  mailboxReads: Schema.optional(Schema.Array(WorkflowMailboxRead)),
  /**
   * "approver" when the reader sees only the steps before the Approvals that
   * name them: then `status` says only whether one of those steps waits
   * ("waiting") or not ("running"), never how the run ended, and `error` and
   * `finishedAt` stay null. Absent when the reader sees the whole run.
   */
  sight: Schema.optional(Schema.Literal("approver")),
});
export type WorkflowRun = typeof WorkflowRun.Type;

/**
 * When a workflow runs by itself: the automations' schedule rules (validated
 * and described by `@repo/shared/automationSchedule`). Kept loose here; the
 * server normalizes it and refuses one that is not a schedule.
 */
export const WorkflowScheduleRule = Schema.Struct({
  frequency: Schema.Literals(["once", "interval", "daily", "weekdays", "weekly", "monthly"]),
  time: Schema.String.check(Schema.isMaxLength(5)),
  timezone: Schema.String.check(Schema.isMaxLength(64)),
  date: Schema.optional(Schema.String.check(Schema.isMaxLength(10))),
  everyMinutes: Schema.optional(Schema.Number),
  until: Schema.optional(Schema.String.check(Schema.isMaxLength(5))),
  weekday: Schema.optional(Schema.Number),
  weekdays: Schema.optional(Schema.Array(Schema.Number).check(Schema.isMaxLength(7))),
  dayOfMonth: Schema.optional(Schema.Number),
  startDate: Schema.optional(Schema.String.check(Schema.isMaxLength(10))),
  endDate: Schema.optional(Schema.String.check(Schema.isMaxLength(10))),
});
export type WorkflowScheduleRule = typeof WorkflowScheduleRule.Type;

/** A workflow's schedule as the page shows it. */
export const WorkflowScheduleView = Schema.Struct({
  rule: WorkflowScheduleRule,
  /** "Weekdays at 9:00 AM". */
  description: Schema.String,
  /** What each scheduled run starts with (nobody is there to type it). */
  input: Schema.String,
  /** The person each scheduled run acts as and is billed to: who turned the schedule on. */
  ownerEmail: Schema.NullOr(Schema.String),
  /** Null once a schedule has no run left (a past one-off, an end date gone). */
  nextRunAt: Schema.NullOr(IsoDateTime),
  /** Why the last scheduled run could not start, until one does. */
  lastError: Schema.NullOr(Schema.String),
});
export type WorkflowScheduleView = typeof WorkflowScheduleView.Type;

/**
 * What a person may do with a workflow (AMO-170): its creator (`owner`)
 * shares and deletes it; `edit` changes and schedules it; `run` runs it.
 * Everyone who may see it may run it, and sees only their own runs, the
 * owner every run.
 */
export const WorkflowRole = Schema.Literals(["owner", "edit", "run"]);
export type WorkflowRole = typeof WorkflowRole.Type;

/** A role a workflow is shared with. */
export const WorkflowShareRole = Schema.Literals(["run", "edit"]);
export type WorkflowShareRole = typeof WorkflowShareRole.Type;

/**
 * Who a workflow is shared with: everyone in its organization (with a role,
 * or `null` for nobody beyond the people listed), and chosen members.
 */
export const WorkflowSharing = Schema.Struct({
  everyone: Schema.NullOr(WorkflowShareRole),
  people: Schema.Array(
    Schema.Struct({ email: Schema.String.check(Schema.isMaxLength(320)), role: WorkflowShareRole }),
  ).check(Schema.isMaxLength(WORKFLOW_LIMITS.sharedPeople)),
});
export type WorkflowSharing = typeof WorkflowSharing.Type;

/** A locked workflow (AMO-305): nobody edits it until it is unlocked; it still runs. */
export const WorkflowLock = Schema.Struct({
  lockedByEmail: Schema.NullOr(Schema.String),
  lockedAt: IsoDateTime,
  reason: Schema.NullOr(Schema.String),
});
export type WorkflowLock = typeof WorkflowLock.Type;

/** One lock or unlock, as a workflow's history keeps it. */
export const WorkflowLockEvent = Schema.Struct({
  action: Schema.Literals(["lock", "unlock"]),
  byEmail: Schema.NullOr(Schema.String),
  reason: Schema.NullOr(Schema.String),
  at: IsoDateTime,
});
export type WorkflowLockEvent = typeof WorkflowLockEvent.Type;

export const WorkflowSummary = Schema.Struct({
  id: Schema.String,
  organizationId: Schema.String,
  spaceId: Schema.NullOr(Schema.String),
  name: Schema.String,
  description: Schema.NullOr(Schema.String),
  graphVersion: NonNegativeInt,
  stepCount: NonNegativeInt,
  createdByEmail: Schema.NullOr(Schema.String),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  lastRun: Schema.NullOr(WorkflowRunSummary),
  /** Absent from older servers; null when the workflow runs only when someone presses Run. */
  schedule: Schema.optional(Schema.NullOr(WorkflowScheduleView)),
  /** What the reader may do with it; absent from older servers (everyone could edit). */
  role: Schema.optional(WorkflowRole),
  /** Null when it can be edited; absent from older servers, which had no locks. */
  lock: Schema.optional(Schema.NullOr(WorkflowLock)),
});
export type WorkflowSummary = typeof WorkflowSummary.Type;

export const Workflow = Schema.Struct({
  ...WorkflowSummary.fields,
  graph: WorkflowGraph,
});
export type Workflow = typeof Workflow.Type;

export const WorkflowListResponse = Schema.Struct({ workflows: Schema.Array(WorkflowSummary) });
export type WorkflowListResponse = typeof WorkflowListResponse.Type;

export const WorkflowDetailResponse = Schema.Struct({
  workflow: Workflow,
  /** The reader's own runs; every run for its owner. */
  runs: Schema.Array(WorkflowRunSummary),
  /** Who it is shared with: for its owner only. */
  sharing: Schema.optional(WorkflowSharing),
  /** Its locks and unlocks, newest first: for those who may edit it. */
  lockHistory: Schema.optional(Schema.Array(WorkflowLockEvent)),
});
export type WorkflowDetailResponse = typeof WorkflowDetailResponse.Type;

/** Locks a workflow, with an optional reason, or unlocks it. */
export const WorkflowLockRequest = Schema.Struct({
  locked: Schema.Boolean,
  reason: Schema.optional(Schema.String.check(Schema.isMaxLength(WORKFLOW_LIMITS.lockReasonLength))),
});
export type WorkflowLockRequest = typeof WorkflowLockRequest.Type;

export const WorkflowLockResponse = Schema.Struct({
  lock: Schema.NullOr(WorkflowLock),
  lockHistory: Schema.Array(WorkflowLockEvent),
});
export type WorkflowLockResponse = typeof WorkflowLockResponse.Type;

export const WorkflowRunResponse = Schema.Struct({ run: WorkflowRun });
export type WorkflowRunResponse = typeof WorkflowRunResponse.Type;

export const WorkflowCreateRequest = Schema.Struct({
  name: TrimmedNonEmptyString.check(Schema.isMaxLength(WORKFLOW_LIMITS.nameLength)),
  description: Schema.optional(Schema.String.check(Schema.isMaxLength(WORKFLOW_LIMITS.descriptionLength))),
  spaceId: Schema.optional(Schema.NullOr(TrimmedNonEmptyString.check(Schema.isMaxLength(128)))),
  /** Absent: a starter graph (Start → Prompt → Output). */
  graph: Schema.optional(WorkflowGraph),
});
export type WorkflowCreateRequest = typeof WorkflowCreateRequest.Type;

/**
 * Saving names the version it was edited from; a save over someone else's
 * newer version is refused (409) instead of silently overwriting it. A
 * `saveId` the saver chose makes the save safe to send again: a repeat is
 * answered with the version it already made.
 */
export const WorkflowSaveRequest = Schema.Struct({
  name: Schema.optional(TrimmedNonEmptyString.check(Schema.isMaxLength(WORKFLOW_LIMITS.nameLength))),
  description: Schema.optional(
    Schema.NullOr(Schema.String.check(Schema.isMaxLength(WORKFLOW_LIMITS.descriptionLength))),
  ),
  graph: Schema.optional(WorkflowGraph),
  expectedGraphVersion: NonNegativeInt,
  saveId: Schema.optional(TrimmedNonEmptyString.check(Schema.isMaxLength(128))),
});
export type WorkflowSaveRequest = typeof WorkflowSaveRequest.Type;

/** Turns a schedule on (or changes it) as the signed-in person, or off with `rule: null`. */
export const WorkflowScheduleRequest = Schema.Struct({
  rule: Schema.NullOr(WorkflowScheduleRule),
  input: Schema.optional(Schema.String.check(Schema.isMaxLength(WORKFLOW_LIMITS.inputLength))),
});
export type WorkflowScheduleRequest = typeof WorkflowScheduleRequest.Type;

/**
 * An approver's decision on a waiting Approval step, naming the attempt they
 * saw. Reject needs a reason; feedback needs a comment and an earlier step.
 */
export const WorkflowDecideRequest = Schema.Struct({
  attempt: NonNegativeInt,
  decision: WorkflowApprovalDecisionKind,
  comment: Schema.optional(Schema.String.check(Schema.isMaxLength(WORKFLOW_LIMITS.commentLength))),
  rerunFrom: Schema.optional(WorkflowElementId),
});
export type WorkflowDecideRequest = typeof WorkflowDecideRequest.Type;

/** The viewer's own mailbox permissions on one workflow, and which mailboxes they have connected. */
export const WorkflowMailboxAccessResponse = Schema.Struct({
  /** Apps this workflow may read, by itself, in the viewer's mailbox. */
  always: Schema.Array(WorkflowMailApp),
  /** The viewer's connected mailboxes, and whether each is ready to read. */
  connected: Schema.Array(Schema.Struct({ app: WorkflowMailApp, ready: Schema.Boolean, accountLabel: Schema.NullOr(Schema.String) })),
});
export type WorkflowMailboxAccessResponse = typeof WorkflowMailboxAccessResponse.Type;

/**
 * Lets this workflow read the viewer's own mailbox in `app`: for every run
 * (`always`), for one waiting run of theirs (`runId`), or no longer
 * (`allow: false`, withdrawing the standing permission).
 */
export const WorkflowMailboxAccessRequest = Schema.Struct({
  app: WorkflowMailApp,
  allow: Schema.Boolean,
  runId: Schema.optional(Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(100))),
});
export type WorkflowMailboxAccessRequest = typeof WorkflowMailboxAccessRequest.Type;

/**
 * Fields suggested for an Ingestion step from a sample file's text (read in
 * the browser), for the person to edit. Nothing is kept.
 */
export const WorkflowSuggestFieldsRequest = Schema.Struct({
  text: Schema.String.check(Schema.isMaxLength(WORKFLOW_LIMITS.sampleTextLength)),
  fileName: Schema.optional(Schema.String.check(Schema.isMaxLength(255))),
});
export type WorkflowSuggestFieldsRequest = typeof WorkflowSuggestFieldsRequest.Type;

export const WorkflowSuggestFieldsResponse = Schema.Struct({
  fields: Schema.Array(WorkflowIngestionField),
});
export type WorkflowSuggestFieldsResponse = typeof WorkflowSuggestFieldsResponse.Type;

/** A file handed to a run (AMO-304): its name, type and bytes as base64. */
export const WorkflowRunFile = Schema.Struct({
  name: Schema.String.check(Schema.isMaxLength(255)),
  mimeType: Schema.NullOr(Schema.String.check(Schema.isMaxLength(200))),
  /** Base64; at most `WORKFLOW_LIMITS.runFileBytes` once decoded. */
  data: Schema.String.check(Schema.isMaxLength(Math.ceil((WORKFLOW_LIMITS.runFileBytes * 4) / 3) + 8)),
});
export type WorkflowRunFile = typeof WorkflowRunFile.Type;

export const WorkflowStartRunRequest = Schema.Struct({
  input: Schema.optional(Schema.String.check(Schema.isMaxLength(WORKFLOW_LIMITS.inputLength))),
  /** Files for the run's Start step to pass on (AMO-304). */
  files: Schema.optional(Schema.Array(WorkflowRunFile).check(Schema.isMaxLength(WORKFLOW_LIMITS.runFiles))),
});
export type WorkflowStartRunRequest = typeof WorkflowStartRunRequest.Type;

/** A run that cannot start because its graph is not runnable, with every reason. */
export const WorkflowInvalidGraphResponse = Schema.Struct({
  message: Schema.String,
  issues: Schema.Array(WorkflowGraphIssue),
});
export type WorkflowInvalidGraphResponse = typeof WorkflowInvalidGraphResponse.Type;

/**
 * A chat opened from a workflow's Ask eve panel (`amoreg_thread_workflows`).
 * A thread belongs to the last workflow it was opened from; `boundAt` moves
 * each time the person opens it there again. `started` is false for a chat
 * bound before its first message was sent, whose thread does not exist yet.
 */
export const WorkflowChat = Schema.Struct({
  threadId: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(100)),
  boundAt: Schema.String,
  started: Schema.Boolean,
});
export type WorkflowChat = typeof WorkflowChat.Type;

/** The person's own chats opened from this workflow, newest `boundAt` first. */
export const WorkflowChatsResponse = Schema.Struct({
  chats: Schema.Array(WorkflowChat),
});
export type WorkflowChatsResponse = typeof WorkflowChatsResponse.Type;

export const WorkflowChatBindRequest = Schema.Struct({
  threadId: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(100)),
});
export type WorkflowChatBindRequest = typeof WorkflowChatBindRequest.Type;

/** A step Run would refuse as it is: its label and what it is missing. */
export const WorkflowFillIn = Schema.Struct({
  stepId: Schema.NullOr(Schema.String),
  step: Schema.NullOr(Schema.String),
  message: Schema.String,
});
export type WorkflowFillIn = typeof WorkflowFillIn.Type;

/**
 * What one of eve's workflow tool calls touched, carried on its tool row
 * (`data.workflow`) so the chat can name the workflow and, after the
 * request's last write, show its receipt. `steps` are the steps a write
 * added, edited or removed; `fillIn` is what Run would refuse after it.
 */
export const WorkflowToolOutcome = Schema.Union([
  Schema.Struct({ kind: Schema.Literal("read"), workflowId: Schema.String, name: Schema.String }),
  Schema.Struct({
    kind: Schema.Literal("write"),
    workflowId: Schema.String,
    name: Schema.String,
    steps: Schema.Array(Schema.String),
    fillIn: Schema.Array(WorkflowFillIn),
  }),
  Schema.Struct({ kind: Schema.Literal("undo"), workflowId: Schema.String, name: Schema.String }),
]);
export type WorkflowToolOutcome = typeof WorkflowToolOutcome.Type;
